/**
 * „Mangel anlegen" — für den Disponenten. Die Meldung aus der App braucht die Anlage in der Hand
 * (QR-Code); hier sucht er die Anlage im Bestand des Mandanten und setzt Klasse, Verantwortlichen
 * und Betriebszustand gleich mit. Der Server legt einen Mangel des Disponenten sofort `open` an,
 * mit der Frist aus der Fristtabelle (tri-server docs/defects.md).
 *
 * Gesucht wird auf dem Server (`POST /query`, höchstens 20 Treffer), nicht in einer geladenen
 * Liste: Ein Mandant hat Tausende Anlagen. Wie in den Anlagenlisten nur Hauptanlagen
 * (`parentId` leer) und nur solche in Betrieb (`assetListScopeFilters`).
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { Asset, and, from, is, or } from "@the-real-insight/tri-model";

import { userAPI } from "../../UserAPI";
import { useDefectsAPI, type Defect } from "../../DefectsAPI";
import { assetListScopeFilters } from "../../components/assetListScope";
import { entityIdAsString } from "../../utilities/buildingLookup";
import {
  EMPTY_NEW_DEFECT,
  OPERATIONS,
  SEVERITIES,
  SEVERITY_LABEL,
  assetOptionLabel,
  canCreateDefect,
  createDefectPayload,
  userLabel,
  type NewDefectDraft,
} from "./defectModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const ASSET_SEARCH_LIMIT = 20;
/** Radix-Select kennt keinen leeren Wert — „niemand" ist deshalb ein eigener. */
const NOBODY = "__nobody__";

interface AssetOption {
  id: string;
  label: string;
}

/** Die Anlagen des Mandanten, deren Name oder Kennung so beginnt. */
const searchAssets = async (customer: string, text: string): Promise<AssetOption[]> => {
  const search = text.trim();
  const filters: any[] = [is("_tenant").equal(customer), is("parentId").equal(null as any), ...assetListScopeFilters()];
  if (search) filters.push(or(is("name").startsWith(search), is("id").startsWith(search)));

  const rows = await userAPI.query(from(Asset.type).where(and(...filters)), 0, ASSET_SEARCH_LIMIT);

  return (Array.isArray(rows) ? rows : [])
    .map((asset: any) => ({ id: entityIdAsString(asset?._id), label: assetOptionLabel(asset) }))
    .filter((option): option is AssetOption => !!option.id);
};

function AssetSearch({
  customer,
  value,
  onChange,
}: {
  customer: string;
  value?: AssetOption;
  onChange: (option: AssetOption) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [options, setOptions] = useState<AssetOption[]>([]);
  const [searching, setSearching] = useState(false);

  // Erst nach einer Tipppause fragen, und nur die letzte Antwort zählt.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      searchAssets(customer, text)
        .then((found) => !cancelled && setOptions(found))
        .catch(() => !cancelled && setOptions([]))
        .finally(() => !cancelled && setSearching(false));
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customer, text, open]);

  // Inline, not a popover: a popover portals to <body>, outside the dialog — its search field and
  // the dialog's focus trap fought over the focus (the list closed the moment it opened), and as a
  // modal popover the chevron no longer closed it. Here everything lives inside the dialog.
  const close = () => {
    setOpen(false);
    setText("");
  };
  return (
    <Command shouldFilter={false} className="h-auto overflow-visible rounded-md border border-input">
      <div className="relative">
        <CommandInput
          aria-label={t("defectsPanel.create.asset")}
          // cmdk pins aria-expanded to true, so the dialog reads its own flag (onEscapeKeyDown).
          data-list-open={open ? "true" : undefined}
          placeholder={value?.label ?? t("defectsPanel.create.searchAsset")}
          className={cn(value && !open && "placeholder:text-foreground")}
          value={text}
          onValueChange={(next) => {
            setText(next);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={close}
          onKeyDown={(event) => {
            // Escape closes the list; the dialog lets it through while the list is open (onEscapeKeyDown).
            if (event.key === "Escape" && open) close();
          }}
        />
        <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
      </div>
      {open ? (
        // mousedown would blur the field (and close the list) before the click selects an entry.
        <CommandList onMouseDown={(event) => event.preventDefault()}>
          {/* Gefiltert hat schon der Server; cmdk soll nicht noch einmal nach seiner Regel aussortieren. */}
          <CommandEmpty>{searching ? "…" : t("defectsPanel.create.noAsset")}</CommandEmpty>
          <CommandGroup>
            {options.map((option) => (
              <CommandItem
                key={option.id}
                value={option.id}
                onSelect={() => {
                  onChange(option);
                  close();
                  (document.activeElement as HTMLElement | null)?.blur();
                }}
              >
                <Check aria-hidden="true" className={cn("mr-2 h-4 w-4 shrink-0", option.id === value?.id ? "opacity-100" : "opacity-0")} />
                <span className="flex-1 whitespace-normal">{option.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      ) : null}
    </Command>
  );
}

interface Props {
  open: boolean;
  customer: string;
  /** `null`, solange die Nutzerliste lädt. */
  users: Record<string, any> | null;
  onClose: () => void;
  /** Der neue Mangel, wie der Server ihn zurückgibt. */
  onCreated: (defect: Defect) => void;
}

export default function CreateDefectDialog({ open, customer, users, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const defectsAPI = useDefectsAPI(customer);
  const [draft, setDraft] = useState<NewDefectDraft>(EMPTY_NEW_DEFECT);
  const [asset, setAsset] = useState<AssetOption | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    setDraft(EMPTY_NEW_DEFECT);
    setAsset(undefined);
    setError(undefined);
  }, [open]);

  const userOptions = useMemo(
    () =>
      Object.keys(users ?? {})
        .map((id) => ({ id, label: userLabel(users, id, { system: t("defectsPanel.system"), unknown: t("defectsPanel.unknownUser"), loading: "…", empty: "—" }) }))
        .sort((a, b) => a.label.localeCompare(b.label, "de")),
    [users, t],
  );

  const change = (next: Partial<NewDefectDraft>) => setDraft((current) => ({ ...current, ...next }));

  const submit = async () => {
    setSubmitting(true);
    setError(undefined);

    try {
      const created = await defectsAPI.createDefect(createDefectPayload(draft));
      toast.success(t("defectsPanel.create.done", { mark: created.mark ?? "" }));
      onCreated(created);
    } catch (createError: any) {
      setError(createError?.message ?? String(createError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        // Radix hears Escape on the document before the field does: with the asset list open, Escape
        // closes that list (AssetSearch), not the whole dialog and what was typed into it.
        onEscapeKeyDown={(event) => {
          if ((event.target as HTMLElement | null)?.getAttribute?.("data-list-open") === "true") event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t("defectsPanel.create.title")}</DialogTitle>
          <DialogDescription>{t("defectsPanel.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Label>{t("defectsPanel.create.asset")}</Label>
            <AssetSearch
              customer={customer}
              value={asset}
              onChange={(option) => {
                setAsset(option);
                change({ asset: option.id });
              }}
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="defect-create-title">{t("defectsPanel.create.titleField")}</Label>
            <Input id="defect-create-title" value={draft.title ?? ""} maxLength={200} onChange={(e) => change({ title: e.target.value })} />
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="defect-create-description">{t("defectsPanel.create.descriptionField")}</Label>
            <Textarea id="defect-create-description" value={draft.description ?? ""} onChange={(e) => change({ description: e.target.value })} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.form.severity")}</Label>
              <Select value={draft.severity} onValueChange={(severity) => change({ severity: severity as any })}>
                <SelectTrigger aria-label={t("defectsPanel.form.severity")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEVERITIES.map((severity) => (
                    <SelectItem key={severity} value={severity}>
                      {SEVERITY_LABEL[severity]} — {t(`defectsPanel.severity.${severity}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label>{t("defectsPanel.create.operation")}</Label>
              <Select value={draft.operation} onValueChange={(operation) => change({ operation: operation as any })}>
                <SelectTrigger aria-label={t("defectsPanel.create.operation")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPERATIONS.map((operation) => (
                    <SelectItem key={operation} value={operation}>
                      {t(`defectsPanel.operation.${operation}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <Label>{t("defectsPanel.form.assignee")}</Label>
            <Select value={draft.assignee ?? NOBODY} onValueChange={(assignee) => change({ assignee: assignee === NOBODY ? undefined : assignee })}>
              <SelectTrigger aria-label={t("defectsPanel.form.assignee")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>{t("defectsPanel.create.nobody")}</SelectItem>
                {userOptions.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("defectsPanel.cancel")}
          </Button>
          <Button disabled={submitting || !canCreateDefect(draft)} onClick={() => void submit()}>
            {t("defectsPanel.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
